import React, { useState, useEffect, useCallback } from 'react';
import {
  Bot, Send, Loader2, History, AlertCircle, PieChart, Activity,
  Cpu, Users, Plus, X, CheckSquare, Square, Search, FileText,
  ChevronRight, Layers
} from 'lucide-react';

// ─── Aba: Análise Individual ─────────────────────────────────────────────────
const AbaIndividual = ({ apiBaseUrl, onNovaAnalise }) => {
  const [prompt, setPrompt] = useState('');
  const [tipo, setTipo] = useState('pesquisa');
  const [loading, setLoading] = useState(false);
  const [respostaAtual, setRespostaAtual] = useState(null);
  const [erro, setErro] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    setLoading(true);
    setErro('');
    setRespostaAtual(null);
    try {
      const res = await fetch(`${apiBaseUrl}/ia/analisar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo, prompt })
      });
      if (!res.ok) throw new Error('Falha ao processar solicitação.');
      const data = await res.json();
      setRespostaAtual(data);
      onNovaAnalise();
    } catch (err) {
      setErro(err.message || 'Erro de comunicação com a IA.');
    } finally {
      setLoading(false);
    }
  };

  const tipos = [
    { id: 'pesquisa', label: 'Pesquisa Geral', icon: <Search size={14} /> },
    { id: 'resumo', label: 'Resumo Jurídico', icon: <FileText size={14} /> },
    { id: 'peticao', label: 'Minuta de Petição', icon: <FileText size={14} /> },
    { id: 'risco', label: 'Análise de Risco', icon: <AlertCircle size={14} /> },
    { id: 'financeiro', label: 'Estratégia Financeira', icon: <PieChart size={14} /> },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Tipo de Análise</label>
            <div className="flex flex-wrap gap-2">
              {tipos.map(opt => (
                <button key={opt.id} type="button" onClick={() => setTipo(opt.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all border ${tipo === opt.id ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  {opt.icon} {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Seu Pedido (Prompt)</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ex: Quais são os maiores riscos nos processos trabalhistas da empresa ABC?"
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 transition-all resize-none h-32" />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={loading || !prompt.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-blue-200 transition-all active:scale-95 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              {loading ? 'Processando...' : 'Analisar com IA'}
            </button>
          </div>
        </form>
        {erro && (
          <div className="mt-4 p-4 bg-red-50 text-red-600 rounded-xl flex items-center gap-2 font-medium text-sm border border-red-100">
            <AlertCircle size={18} /> {erro}
          </div>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 flex flex-col items-center justify-center text-slate-400 gap-4 shadow-sm">
          <Cpu className="animate-pulse text-blue-600" size={48} />
          <p className="font-medium">Cruzando dados internos e gerando insights...</p>
        </div>
      ) : respostaAtual ? (
        <ResultadoIA dados={respostaAtual} />
      ) : null}
    </div>
  );
};

// ─── Aba: Análise em Grupo ───────────────────────────────────────────────────
const AbaGrupo = ({ apiBaseUrl, onNovaAnalise }) => {
  const [todos, setTodos] = useState([]);
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState([]);
  const [contexto, setContexto] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProcessos, setLoadingProcessos] = useState(true);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    const carregarProcessos = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/processos`);
        if (res.ok) {
          const data = await res.json();
          setTodos(data);
        }
      } catch (e) {
        console.error('Erro ao carregar processos:', e);
      } finally {
        setLoadingProcessos(false);
      }
    };
    carregarProcessos();
  }, [apiBaseUrl]);

  const filtrados = todos.filter(p => {
    const term = busca.toLowerCase();
    return !term || (p.numero || '').toLowerCase().includes(term) ||
      (p.cliente || '').toLowerCase().includes(term) ||
      (p.status || '').toLowerCase().includes(term);
  });

  const toggleSelecionado = (processo) => {
    setSelecionados(prev => {
      const jaesta = prev.find(p => p.id === processo.id);
      if (jaesta) {
        setErro('');
        return prev.filter(p => p.id !== processo.id);
      }
      if (prev.length >= 6) {
        setErro('Você só pode selecionar até 6 processos.');
        return prev;
      }
      setErro('');
      return [...prev, processo];
    });
  };

  const handleAnalisar = async () => {
    if (selecionados.length < 2) {
      setErro('Selecione pelo menos 2 processos de clientes diferentes.');
      return;
    }
    setLoading(true);
    setErro('');
    setResultado(null);
    try {
      const res = await fetch(`${apiBaseUrl}/api/ia/analisar-grupo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selecionados.map(p => p.id), contexto_adicional: contexto })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Falha na análise.');
      }
      const data = await res.json();
      setResultado(data);
      onNovaAnalise();
    } catch (err) {
      setErro(err.message || 'Erro ao processar análise em grupo.');
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (status) => {
    if (!status) return 'bg-slate-100 text-slate-500';
    const s = status.toLowerCase();
    if (s.includes('ativo') || s.includes('andamento')) return 'bg-blue-50 text-blue-600';
    if (s.includes('conclu') || s.includes('ganho') || s.includes('favorá')) return 'bg-emerald-50 text-emerald-600';
    if (s.includes('arquiv') || s.includes('perdido') || s.includes('desfavor')) return 'bg-red-50 text-red-600';
    return 'bg-amber-50 text-amber-600';
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Seletor de Processos */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col" style={{ maxHeight: 520 }}>
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Search size={16} className="text-blue-600" /> Selecionar Processos
            </h3>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" value={busca} onChange={e => setBusca(e.target.value)}
                placeholder="Buscar por número, cliente ou status..."
                className="w-full pl-8 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all" />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 p-2">
            {loadingProcessos ? (
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 className="animate-spin mr-2" size={18} /> Carregando processos...
              </div>
            ) : filtrados.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Nenhum processo encontrado.</p>
            ) : (
              filtrados.map(p => {
                const sel = selecionados.find(s => s.id === p.id);
                return (
                  <div key={p.id} onClick={() => toggleSelecionado(p)}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all mb-1 border ${sel ? 'bg-blue-50 border-blue-200' : 'hover:bg-slate-50 border-transparent'}`}>
                    <div className={`shrink-0 ${sel ? 'text-blue-600' : 'text-slate-300'}`}>
                      {sel ? <CheckSquare size={18} /> : <Square size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 text-sm truncate">{p.numero}</p>
                      <p className="text-xs text-slate-500 truncate">{p.cliente}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md shrink-0 ${statusColor(p.status)}`}>
                      {p.status || 'Sem status'}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Painel de Configuração */}
        <div className="space-y-4">
          {/* Processos Selecionados */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Layers size={16} className="text-blue-600" /> Grupo Selecionado
              <span className={`ml-auto text-xs font-bold px-2 py-1 rounded-lg ${selecionados.length >= 2 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                {selecionados.length}/6 processo(s) {selecionados.length >= 2 ? '✓' : '(mín. 2)'}
              </span>
            </h3>
            {selecionados.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">
                Clique nos processos ao lado para adicioná-los ao grupo.
              </p>
            ) : (
              <div className="space-y-2">
                {selecionados.map(p => (
                  <div key={p.id} className="flex items-center gap-2 bg-blue-50 p-2.5 rounded-xl border border-blue-100">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 text-xs truncate">{p.numero}</p>
                      <p className="text-[10px] text-slate-500 truncate">{p.cliente}</p>
                    </div>
                    <button onClick={() => toggleSelecionado(p)} className="shrink-0 text-slate-400 hover:text-red-500 transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contexto Adicional */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
              Contexto Adicional (opcional)
            </label>
            <textarea value={contexto} onChange={e => setContexto(e.target.value)}
              placeholder="Ex: Crie uma petição com o melhor de cada despacho/sentença dos processos com êxito."
              className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400 transition-all resize-none h-24" />
          </div>

          {erro && (
            <div className="p-3 bg-red-50 text-red-600 rounded-xl flex items-center gap-2 text-sm border border-red-100">
              <AlertCircle size={16} /> {erro}
            </div>
          )}

          <button onClick={handleAnalisar} disabled={loading || selecionados.length < 2}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3.5 rounded-xl font-bold shadow-lg shadow-blue-200 transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Cpu size={18} />}
            {loading ? 'Analisando grupo...' : `Gerar Análise Comparativa (${selecionados.length} processos)`}
          </button>
        </div>
      </div>

      {/* Resultado da Análise em Grupo */}
      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 flex flex-col items-center justify-center text-slate-400 gap-4 shadow-sm">
          <Cpu className="animate-pulse text-indigo-600" size={48} />
          <p className="font-medium">Comparando processos e gerando nova petição otimizada...</p>
          <p className="text-sm text-slate-300">Analisando êxitos, falhas e oportunidades de melhoria...</p>
        </div>
      )}
      {resultado && !loading && <ResultadoIA dados={resultado} isGrupo />}
    </div>
  );
};

// ─── Componente Compartilhado: Resultado da IA ───────────────────────────────
const ResultadoIA = ({ dados, isGrupo }) => (
  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 shadow-xl overflow-hidden">
    <div className="p-4 border-b border-white/10 flex items-center gap-3 bg-white/5">
      {isGrupo ? <Layers className="text-indigo-400" size={20} /> : <Bot className="text-blue-400" size={20} />}
      <h3 className="text-white font-bold text-sm tracking-wide">
        {isGrupo ? `Análise Comparativa — ${dados.processos_analisados?.length || ''} processos` : 'Resposta da Legal AI'}
      </h3>
      {isGrupo && dados.processos_analisados && (
        <div className="ml-auto flex gap-1 flex-wrap justify-end">
          {dados.processos_analisados.slice(0, 3).map(p => (
            <span key={p.id} className="text-[10px] bg-white/10 text-slate-300 px-2 py-0.5 rounded-md font-bold">
              {p.cliente}
            </span>
          ))}
          {dados.processos_analisados.length > 3 && (
            <span className="text-[10px] bg-white/10 text-slate-300 px-2 py-0.5 rounded-md font-bold">
              +{dados.processos_analisados.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
    <div className="p-6 text-slate-300 text-sm leading-relaxed whitespace-pre-wrap font-mono max-h-[600px] overflow-y-auto">
      {dados.resposta}
    </div>
    {!isGrupo && dados.processos_usados && dados.processos_usados.length > 0 && (
      <div className="bg-black/20 p-4 border-t border-white/5">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Fontes Utilizadas</p>
        <div className="flex gap-2 flex-wrap">
          {dados.processos_usados.map(p => (
            <div key={p.id} className="bg-white/10 px-3 py-2 rounded-lg border border-white/10">
              <p className="text-xs font-bold text-white">{p.numero}</p>
              <p className="text-[10px] text-slate-400">{p.cliente}</p>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// ─── Módulo Principal: Legal AI ──────────────────────────────────────────────
const LegalAIModule = ({ apiBaseUrl }) => {
  const [abaAtiva, setAbaAtiva] = useState('individual');
  const [historico, setHistorico] = useState([]);
  const [respostaHistorico, setRespostaHistorico] = useState(null);

  const loadHistorico = useCallback(async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/ia/historico`);
      if (res.ok) setHistorico(await res.json());
    } catch (e) {
      console.error('Erro ao carregar histórico:', e);
    }
  }, [apiBaseUrl]);

  useEffect(() => { loadHistorico(); }, [loadHistorico]);

  const abas = [
    { id: 'individual', label: 'Análise Individual', icon: <Bot size={16} /> },
    { id: 'grupo', label: 'Análise em Grupo', icon: <Users size={16} /> },
  ];

  const tipoLabel = (tipo) => {
    const map = { pesquisa: 'Pesquisa', resumo: 'Resumo', peticao: 'Petição', risco: 'Risco', financeiro: 'Financeiro', grupo: 'Em Grupo' };
    return map[tipo] || tipo;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-3">
          <Bot className="text-blue-600" size={28} /> Legal AI — Memória Interna
        </h2>
        <p className="text-slate-500 text-sm font-medium mt-1">
          Análise inteligente individual ou comparativa entre múltiplos processos de diferentes clientes.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Principal */}
        <div className="lg:col-span-2 space-y-4">
          {/* Abas */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 flex gap-1">
            {abas.map(aba => (
              <button key={aba.id} onClick={() => setAbaAtiva(aba.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm transition-all ${abaAtiva === aba.id ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}>
                {aba.icon} {aba.label}
              </button>
            ))}
          </div>

          {abaAtiva === 'individual' && (
            <AbaIndividual apiBaseUrl={apiBaseUrl} onNovaAnalise={loadHistorico} />
          )}
          {abaAtiva === 'grupo' && (
            <AbaGrupo apiBaseUrl={apiBaseUrl} onNovaAnalise={loadHistorico} />
          )}
        </div>

        {/* Histórico */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm h-fit max-h-[750px] flex flex-col">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <History size={16} className="text-blue-600" />
            <h3 className="font-bold text-sm text-slate-800">Histórico de Análises</h3>
          </div>
          <div className="overflow-y-auto flex-1 p-3 space-y-3">
            {historico.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Nenhuma análise anterior.</p>
            ) : historico.map(item => (
              <div key={item.id}
                className="p-3 bg-slate-50 border border-slate-100 rounded-xl cursor-pointer hover:border-blue-200 transition-colors"
                onClick={() => setRespostaHistorico({ resposta: item.resposta, processos_usados: [] })}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${item.tipo === 'grupo' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-white text-slate-500 border-slate-200'}`}>
                    {item.tipo === 'grupo' ? <span className="flex items-center gap-1"><Users size={10} /> {tipoLabel(item.tipo)}</span> : tipoLabel(item.tipo)}
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">
                    {new Date(item.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-700 line-clamp-2 leading-snug">
                  "{item.prompt}"
                </p>
                <div className="flex items-center gap-1 mt-2 text-blue-500 text-[10px] font-bold">
                  <ChevronRight size={10} /> Ver resultado
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal de resultado do histórico */}
      {respostaHistorico && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={() => setRespostaHistorico(null)}>
          <div className="bg-slate-900 rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <span className="text-white font-bold flex items-center gap-2"><History size={16} /> Resultado do Histórico</span>
              <button onClick={() => setRespostaHistorico(null)} className="text-slate-400 hover:text-white transition-colors"><X size={18} /></button>
            </div>
            <div className="p-6 text-slate-300 text-sm leading-relaxed whitespace-pre-wrap font-mono overflow-y-auto max-h-[60vh]">
              {respostaHistorico.resposta}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalAIModule;
